<!--
  Страница скачивания приложения

  Функционал:
  1. Автоматически определяет устройство пользователя (iOS/Android/Desktop)
  2. Пытается выполнить автоматический редирект на соответствующий маркетплейс
  3. Если редирект не сработал - показывает кнопки для ручного перехода (Plan B)

  Использование:
  - Пользователь переходит на /app-download
  - Страница загружает данные учреждения с google_play_url и app_store_url
  - Определяется устройство и выполняется редирект
  - Если не удалось - показываются кнопки обоих маркетплейсов

  Ссылки берутся из API: GET /institutions/{id}
  - google_play_url - ссылка на RuStore для Android
  - app_store_url - ссылка на App Store для iOS
-->
<template>
  <div class="app-download-page">
    <UContainer
      :ui="{
        base: 'mx-auto relative',
        padding: 'px-4 sm:px-6 lg:px-8',
        constrained: 'max-w-7xl',
      }"
    >
      <!-- Состояние редиректа -->
      <div v-if="isRedirecting" class="redirect-state">
        <div class="redirect-content">
          <div class="spinner-large"></div>
          <h2 class="redirect-title">Перенаправление...</h2>
          <p class="redirect-description">
            Сейчас вы будете перенаправлены в магазин приложений
          </p>
          <div class="redirect-device-info">
            <Icon name="heroicons:device-phone-mobile" class="w-6 h-6" />
            <span>Определено: {{ deviceTypeLabel }}</span>
          </div>
          <p class="redirect-fallback-hint">
            Если переход не произошел автоматически, используйте кнопки ниже
          </p>
        </div>
      </div>

      <!-- Состояние ошибки -->
      <div v-else-if="hasError" class="error-state">
        <div class="error-content">
          <Icon name="heroicons:exclamation-triangle" class="error-icon" />
          <h2 class="error-title">Произошла ошибка</h2>
          <p class="error-description">
            {{ errorMessage }}
          </p>
          <button class="retry-button" @click="retry">
            <Icon name="heroicons:arrow-path" class="w-5 h-5" />
            Попробовать снова
          </button>
        </div>
      </div>

      <!-- Обычное состояние: кнопки магазинов и текст о приложении.
           Рендерится и на сервере — раньше здесь до маунта висел спиннер
           «Загрузка...», и в исходном HTML страницы, которая лежит в sitemap
           и собирает запрос «скачать приложение <заведение>», не было ни
           заголовка, ни ссылок на магазины: только крутилка. -->
      <div v-else class="buttons-state">
        <AppDownloadButtons
          :android-url="marketplaceUrls.android"
          :ios-url="marketplaceUrls.ios"
          :title="pageTitle"
          :subtitle="pageSubtitle"
          :device-type="deviceInfo.type"
          :show-device-info="isDev"
          @android-click="handleAndroidClick"
          @ios-click="handleIosClick"
        />

        <section class="app-benefits">
          <h2 class="app-benefits__title">Зачем приложение</h2>
          <ul class="app-benefits__list">
            <li v-for="benefit in benefits" :key="benefit" class="app-benefits__item">{{ benefit }}</li>
          </ul>
          <p class="app-benefits__note">
            Заказать можно и без приложения — всё меню
            <NuxtLink to="/" class="app-benefits__link">на сайте</NuxtLink>.
          </p>
        </section>
      </div>
    </UContainer>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola'
import { ref, computed, onMounted } from 'vue'
import { useInstitutionStore , getReadyInstitutionId } from '@entities/institution'
import { useAppDownloadRedirect } from '@features/app-download'


// Stores
const storeInstitution = useInstitutionStore()

// Composables
// showButtons больше не нужен: кнопки магазинов — состояние страницы по
// умолчанию, а не «план Б» после неудачного редиректа. До маунта (то есть в
// серверной разметке) флаг всегда был false, и страница отдавалась пустой.
const {
  isRedirecting,
  deviceInfo,
  getMarketplaceUrls,
  initAutoRedirect,
} = useAppDownloadRedirect()

// Состояние
const isLoading = ref(false)
const hasError = ref(false)
const errorMessage = ref('')

/**
 * Текст о приложении. Нужен и гостю (страница перестала быть пустой крутилкой),
 * и поиску: страница без единого предложения текста конкурирует за запрос
 * «скачать приложение <заведение>» одним только заголовком.
 */
const benefits = [
  'Заказ в два касания: адрес, оплата и любимые блюда уже сохранены',
  'Бонусные баллы за каждый заказ и персональные акции',
  'Статус заказа и время доставки — push-уведомлением, без звонков',
  'История заказов: повторить прошлый — одна кнопка',
]

// Определяем режим разработки
const isDev = computed(() => process.env.NODE_ENV === 'development')

// Лейблы устройства
const deviceTypeLabel = computed(() => {
  switch (deviceInfo.value.type) {
    case 'ios': {
      return 'iOS устройство'
    }
    case 'android': {
      return 'Android устройство'
    }
    case 'desktop': {
      return 'Desktop устройство'
    }
    default: {
      return 'Неизвестное устройство'
    }
  }
})

// Получаем ссылки на маркетплейсы
const marketplaceUrls = computed(() => getMarketplaceUrls())

// Тексты для страницы
const institutionName = computed(() => storeInstitution.institution?.name || 'Наше приложение')

// Город из загруженных филиалов — для информативного SEO-фолбэка
const cityName = computed(() => {
  const city = storeInstitution.currentCity?.name || storeInstitution.cities?.[0]?.name
  return city && city !== 'Не указан' ? city : ''
})

const pageTitle = computed(() => {
  return `Скачайте приложение ${institutionName.value}`
})

const pageSubtitle = computed(() => {
  const device = deviceInfo.value.type
  if (device === 'ios') {
    return 'Доступно в App Store'
  } else if (device === 'android') {
    return 'Доступно в RuStore'
  }
  return 'Доступно на Android и iOS'
})

// Обработчики событий
const handleAndroidClick = () => {
  consola.log('[app-download] Клик на кнопку Android')
}

const handleIosClick = () => {
  consola.log('[app-download] Клик на кнопку iOS')
}

const retry = async () => {
  hasError.value = false
  errorMessage.value = ''
  await initializePage()
}

// Инициализация страницы
const initializePage = async () => {
  try {
    isLoading.value = true
    hasError.value = false

    consola.log('[app-download] Инициализация страницы...')

    // Получаем ID учреждения
    const institutionId = await getReadyInstitutionId()
    consola.log('[app-download] Institution ID:', institutionId)

    // Загружаем данные учреждения если еще не загружены
    if (!storeInstitution.institution || storeInstitution.institution.id !== String(institutionId)) {
      consola.log('[app-download] Загружаем данные учреждения...')
      await storeInstitution.fetchBranches(institutionId)
    }

    // Подробное логирование данных учреждения
    consola.log('[app-download] 📊 Данные учреждения:', {
      hasInstitution: !!storeInstitution.institution,
      institutionId: storeInstitution.institution?.id,
      institutionName: storeInstitution.institution?.name,
      googlePlayUrl: storeInstitution.institution?.google_play_url,
      appStoreUrl: storeInstitution.institution?.app_store_url,
    })

    // Проверяем наличие ссылок
    const urls = getMarketplaceUrls()
    consola.log('[app-download] 🔗 Ссылки на маркетплейсы:', urls)

    if (!urls.android && !urls.ios) {
      consola.warn('[app-download] ❌ Не найдены ссылки на маркетплейсы')
      hasError.value = true
      errorMessage.value = 'Ссылки на скачивание временно недоступны. Попробуйте позже.'
      isLoading.value = false
      return
    }

    consola.log('[app-download] ✅ Ссылки найдены, продолжаем...')

    isLoading.value = false

    // Запускаем автоматический редирект
    consola.log('[app-download] Запускаем автоматический редирект...')
    await initAutoRedirect()

  } catch (error: any) {
    consola.error('[app-download] Ошибка при инициализации:', error)
    hasError.value = true
    errorMessage.value = error.message || 'Не удалось загрузить данные. Попробуйте обновить страницу.'
    isLoading.value = false
  }
}

// SEO мета-теги.
//
// Заголовок и описание собираем ИЗ НАЗНАЧЕНИЯ СТРАНИЦЫ, а не из seo_meta
// заведения. Раньше при заполненной админке страница дословно повторяла title и
// description главной — в индексе оказывались два URL с одинаковым сниппетом,
// и поисковик выбирал между ними сам. Страница про установку приложения должна
// отвечать на свой запрос («скачать приложение <заведение>»), а не на запрос главной.
//
// Город добавляем через запятую, без предлога: названия в БД лежат в
// именительном падеже, склонять их некому.
const seo = computed(() => {
  const inst = storeInstitution.institution as any
  const instMeta = inst?.seo_meta ?? {}
  const name = institutionName.value
  const city = cityName.value

  const citySuffix = city ? `, ${city}` : ''
  const cityNote = city ? ` (${city})` : ''

  const title = `Скачать приложение ${name} для iOS и Android${citySuffix}`

  const description =
    `Приложение ${name}${cityNote} для заказа еды с доставкой на дом или в офис. ` +
    `Установите на iOS или Android: оформляйте заказы быстрее, копите бонусы и получайте персональные акции.`

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage: instMeta.og_image || inst?.visual?.logo_file || '',
  }
})

// Реактивный useHead: данные заведения приходят из SSR-prefetch
// (plugins/institution-ssr.ts), поэтому мета должны пересчитываться, а не
// фиксироваться на момент setup (иначе в HTML попадал дефолт «Наше приложение»).
useHead(() => ({
  title: seo.value.title,
  meta: [
    { name: 'description', content: seo.value.description },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:title', content: seo.value.ogTitle },
    { property: 'og:description', content: seo.value.ogDescription },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: seo.value.ogImage },
  ].filter(meta => meta.content),
}))

// Инициализация при монтировании
onMounted(async () => {
  await initializePage()
})
</script>

<style scoped lang="scss">
.app-download-page {
  @apply min-h-screen flex items-center justify-center
         bg-gradient-to-br from-gray-50 to-gray-100
         dark:from-gray-900 dark:to-gray-800;
}

// Текст о приложении под кнопками магазинов
.app-benefits {
  @apply max-w-2xl mx-auto pt-10 md:pt-14 text-left;

  &__title {
    @apply text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100;
  }

  &__list {
    @apply mt-4 flex flex-col gap-2 list-disc pl-5;
  }

  &__item {
    @apply text-base text-gray-700 dark:text-gray-300;
  }

  &__note {
    @apply mt-6 text-sm text-gray-500 dark:text-gray-400;
  }

  &__link {
    @apply font-semibold underline;
  }
}

// Состояние редиректа
.redirect-state {
  @apply flex items-center justify-center min-h-[60vh];
}

.redirect-content {
  @apply flex flex-col items-center gap-6 text-center max-w-md;
}

.spinner-large {
  @apply w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin;
}

.redirect-title {
  @apply text-3xl font-bold text-gray-900 dark:text-gray-100;
}

.redirect-description {
  @apply text-lg text-gray-600 dark:text-gray-400;
}

.redirect-device-info {
  @apply flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20
         rounded-lg text-blue-600 dark:text-blue-400;
}

.redirect-fallback-hint {
  @apply text-sm text-gray-500 dark:text-gray-500 mt-4;
}

// Кнопки скачивания
.buttons-state {
  @apply py-12;
}

// Состояние ошибки
.error-state {
  @apply flex items-center justify-center min-h-[60vh];
}

.error-content {
  @apply flex flex-col items-center gap-6 text-center max-w-md;
}

.error-icon {
  @apply w-16 h-16 text-red-500;
}

.error-title {
  @apply text-3xl font-bold text-gray-900 dark:text-gray-100;
}

.error-description {
  @apply text-lg text-gray-600 dark:text-gray-400;
}

.retry-button {
  @apply flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700
         text-white rounded-lg transition-colors duration-200
         shadow-lg hover:shadow-xl;
}

// Анимации
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading-state,
.redirect-state,
.buttons-state,
.error-state {
  animation: fadeIn 0.3s ease-out;
}

// Темная тема
.dark {
  .spinner {
    @apply border-gray-700 border-t-blue-500;
  }

  .spinner-large {
    @apply border-gray-700 border-t-blue-500;
  }
}
</style>
